import { useCallback, useMemo, useState } from 'react';
import { defineMessages } from 'react-intl';
import { Box } from '@cbhq/cds-web/layout';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { EditMemberPendingRoleBanner } from '../components/EditMemberTray/EditMemberPendingRoleBanner';
import type { EditMemberStatus } from '../components/EditMemberTray/EditMemberTrayHeader';
import { MemberRecentActivity } from '../components/EditMemberTray/MemberRecentActivity';
import { MultiLEProductAccessGroup } from '../components/InviteTray/MultiLEProductAccessGroup';
import { ProtectedRoleWarning } from '../components/InviteTray/ProtectedRoleWarning';
import { useEditProtectedRoleWarning } from '../components/InviteTray/useProtectedRoleWarning';
import { StepUpTwoFactorModal } from '../components/StepUpTwoFactorModal';
import { UnsavedChangesAlert } from '../components/UnsavedChangesAlert';
import { useEditMemberSave } from '../hooks/useEditMemberSave';
import { useMultiLEMemberAccess } from '../hooks/useMultiLEMemberAccess';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import type { Member as TeamMember, MemberRole, MemberRolesForEntity } from '../utils/constants';
import type { PendingRole } from '../utils/gracePeriod';
import { isGatedRoleMutation } from '../utils/stepUp';
import { getMemberDisplayName } from '../utils/workspaceTeam';

import { EditMemberTray } from './EditMemberTray';

const messages = defineMessages({
  saveButton: {
    id: 'Team.multiLEEditTray.saveButton',
    defaultMessage: 'Save changes',
    description: 'Save button in the multi-LE edit member tray',
  },
  saveSuccess: {
    id: 'Team.multiLEEditTray.saveSuccess',
    defaultMessage: 'Membership updated',
    description: 'Toast shown after successfully saving member role changes',
  },
  saveError: {
    id: 'Team.multiLEEditTray.saveError',
    defaultMessage: 'Error updating membership',
    description: 'Toast shown when saving member role changes fails',
  },
});

type MultiLEEditTrayProps = {
  member: TeamMember;
  /** The member's effective `legalEntityRoles`. They seed the role pickers and the save diff. */
  memberRolesForEntity?: MemberRolesForEntity[];
  /** The member's pending roles, for the pending role banner. */
  pendingRoles?: PendingRole[];
  status: EditMemberStatus;
  joinedLabel?: string;
  lastActiveLabel?: string;
  isEditable?: boolean;
  /**
   * Per-entity override of `isEditable`. The tray lists every entity the current
   * user can see, but only entities they administer may be edited or submitted.
   */
  canEditEntity?: (legalEntityId: string) => boolean;
  onClose: () => void;
};

function allEntitiesEditable() {
  return true;
}

export function MultiLEEditTray({
  member,
  memberRolesForEntity,
  pendingRoles,
  status,
  joinedLabel,
  lastActiveLabel,
  isEditable = true,
  canEditEntity = allEntitiesEditable,
  onClose,
}: MultiLEEditTrayProps) {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';

  // IDFE-4025: bumped after "Revoke admin access" so the selection re-seeds from
  // the member's updated roles (otherwise a later save would resubmit them).
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const handlePendingRoleCancelled = useCallback(() => {
    setSelectionResetKey((key) => key + 1);
  }, []);

  const isEntityEditable = useCallback(
    (legalEntityId: string) => isEditable && canEditEntity(legalEntityId),
    [canEditEntity, isEditable],
  );

  const {
    legalEntityIds,
    supportedRolesSet,
    unassignableRolesByProductPerEntity,
    selection: multiLEProductSelection,
    entities,
  } = useMultiLEMemberAccess({
    memberRolesForEntity,
    isEntityEditable,
    resetKey: selectionResetKey,
  });
  const { hasChanges, getLegalEntityRoles } = multiLEProductSelection;

  const readOnlyEntityIds = useMemo(
    () => legalEntityIds.filter((id) => !isEntityEditable(id)),
    [isEntityEditable, legalEntityIds],
  );

  // Roles outside supportedRoles must be preserved when saving because
  // updateOrganizationMemberRole() replaces the full role set for each entity.
  const hiddenRolesByEntity = useMemo(() => {
    const result = new Map<string, MemberRole[]>();
    for (const ler of memberRolesForEntity ?? []) {
      if (ler.legalEntityId) {
        const hidden = ((ler.roles ?? []) as MemberRole[]).filter(
          (role) => !supportedRolesSet.has(role),
        );
        if (hidden.length > 0) {
          result.set(ler.legalEntityId, hidden);
        }
      }
    }
    return result;
  }, [memberRolesForEntity, supportedRolesSet]);

  // Held unassignable roles seed the selection, so they normally save via the
  // selection. But deselecting an entity clears its selection, so they're also
  // merged back on save to guarantee they're never dropped.
  const heldUnassignableRolesByEntity = useMemo(() => {
    const result = new Map<string, MemberRole[]>();
    for (const [entityId, byProduct] of Object.entries(unassignableRolesByProductPerEntity)) {
      const roles = Object.values(byProduct).flatMap((defs) => defs.map((def) => def.value));
      if (roles.length > 0) {
        result.set(entityId, roles);
      }
    }
    return result;
  }, [unassignableRolesByProductPerEntity]);

  const name = getMemberDisplayName(member);

  const legalEntityRoles = useMemo(() => {
    const rolesByEntity = new Map<string, MemberRole[]>();
    for (const { legalEntityId, roles } of getLegalEntityRoles()) {
      rolesByEntity.set(legalEntityId, [...roles]);
    }

    // Re-add held unassignable roles (deduped) so they survive even a full
    // entity deselect, which clears the selection they were seeded into.
    for (const [legalEntityId, held] of heldUnassignableRolesByEntity) {
      const existing = rolesByEntity.get(legalEntityId) ?? [];
      rolesByEntity.set(legalEntityId, [...new Set([...existing, ...held])]);
    }

    // Roles outside supportedRoles are preserved verbatim because
    // updateOrganizationMemberRole() replaces the full per-entity set.
    for (const [legalEntityId, hidden] of hiddenRolesByEntity) {
      const existing = rolesByEntity.get(legalEntityId) ?? [];
      rolesByEntity.set(legalEntityId, [...existing, ...hidden]);
    }

    return [...rolesByEntity.entries()].map(([legalEntityId, roles]) => ({ legalEntityId, roles }));
  }, [getLegalEntityRoles, heldUnassignableRolesByEntity, hiddenRolesByEntity]);

  const currentLegalEntityRoles = useMemo(
    () =>
      (memberRolesForEntity ?? [])
        .filter((ler): ler is typeof ler & { legalEntityId: string } => Boolean(ler.legalEntityId))
        .map((ler) => ({
          legalEntityId: ler.legalEntityId,
          roles: (ler.roles ?? []) as MemberRole[],
        })),
    [memberRolesForEntity],
  );

  // IDFE-4025 (t0018_4fba688a): warn when a protected role (e.g. Admin) is newly
  // selected on an entity. Roles already held or pending there don't warn again.
  const selectedRolesByEntity = useMemo(
    () =>
      Object.fromEntries(
        legalEntityRoles.map(({ legalEntityId, roles }) => [legalEntityId, roles]),
      ),
    [legalEntityRoles],
  );
  const currentRolesByEntity = useMemo(
    () =>
      Object.fromEntries(
        currentLegalEntityRoles.map(({ legalEntityId, roles }) => [legalEntityId, roles]),
      ),
    [currentLegalEntityRoles],
  );
  const protectedRoleWarning = useEditProtectedRoleWarning(
    selectedRolesByEntity,
    currentRolesByEntity,
  );

  const isGatedMutation = useMemo(
    () => isGatedRoleMutation(currentLegalEntityRoles, legalEntityRoles),
    [currentLegalEntityRoles, legalEntityRoles],
  );

  const {
    footer,
    isSaving,
    stepUpModalProps,
    refreshWorkspaceMembers,
    setIsCancellingPendingRole,
  } = useEditMemberSave({
    organizationId,
    memberUuid: member.userUuid,
    legalEntityRoles,
    readOnlyEntityIds,
    hasChanges,
    isGatedRoleMutation: isGatedMutation,
    isEditable,
    messages,
    onClose,
  });

  // A failed cancel can still revoke some grants. Re-seed from the refetched
  // roles, not the rolled-back ones, so a later save can't re-grant them.
  const handlePendingRoleCancelFailed = useCallback(async () => {
    try {
      await refreshWorkspaceMembers();
    } finally {
      setSelectionResetKey((key) => key + 1);
    }
  }, [refreshWorkspaceMembers]);

  const { trayRef, isAlertVisible, handleBeforeClose, handleKeepEditing, handleDiscard } =
    useUnsavedChangesGuard(hasChanges);

  const recentActivity = useMemo(
    () => <MemberRecentActivity organizationId={organizationId} memberUuid={member.userUuid} />,
    [member.userUuid, organizationId],
  );

  return (
    <>
      <EditMemberTray
        name={name}
        email={member.email}
        status={status}
        joinedLabel={joinedLabel}
        lastActiveLabel={lastActiveLabel}
        avatarSrc={member.avatarUrl}
        trayRef={trayRef}
        footer={footer}
        recentActivity={recentActivity}
        beforeClose={handleBeforeClose}
        onClose={onClose}
      >
        <MultiLEProductAccessGroup entities={entities} isEditable={isEditable} />
        <EditMemberPendingRoleBanner
          memberUuid={member.userUuid}
          pendingRoles={pendingRoles}
          canCancelEntity={isEntityEditable}
          onCancelled={handlePendingRoleCancelled}
          onCancelFailed={handlePendingRoleCancelFailed}
          onCancellingChange={setIsCancellingPendingRole}
          isCancelDisabled={isSaving}
        />
        {protectedRoleWarning != null && (
          <Box paddingTop={2}>
            <ProtectedRoleWarning
              roleName={protectedRoleWarning.roleName}
              gracePeriodMs={protectedRoleWarning.gracePeriodMs}
              context="edit"
            />
          </Box>
        )}
      </EditMemberTray>
      <StepUpTwoFactorModal {...stepUpModalProps} />
      <UnsavedChangesAlert
        variant="edit"
        visible={isAlertVisible}
        memberName={name}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
